import { Popover } from 'antd';
import { useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { focusResaAtom } from '../store/core';
import { ResaLocation, isInOpenHours, locationAtom } from '../store/location';

import classNames from 'classnames';
import {
	FormattedDate,
	FormattedList,
	FormattedMessage,
	FormattedTime,
} from 'react-intl';
import { FocusResa } from '../core/focusResa';
import { User, allUsersAtom, userAtom } from '../store/user';
import './ResaCalendar.scss';
import { UserLabel } from './User';
import {
	CalendarEvent,
	MandatoryCalendarEvent,
	ResaCalendarEvent,
} from './ResaCalendar';
import { LocationLabel } from './Location';

export const EventRenderer = ({ event }: { event: CalendarEvent }) => {
	return event.type == 'mandatory' ? (
		<MandatoryEventRenderer event={event} />
	) : (
		<ResaEventRenderer event={event} />
	);
};
const MandatoryEventRenderer = ({
	event,
}: {
	event: MandatoryCalendarEvent;
}) => {
	return (
		<div className={classNames('event-mandatory')}>
			<FormattedMessage
				defaultMessage="{mandatoryEventInfo} {mandatoryEventStart}-{mandatoryEventEnd}"
				id="O15C9B"
				values={{
					mandatoryEventInfo: event.info,
					mandatoryEventStart: <FormattedTime timeZone="+02:00" value={event.start} />,
					mandatoryEventEnd: <FormattedTime timeZone="+02:00" value={event.end} />,
				}}
			/>
		</div>
	);
};
const ResaEventRenderer = ({ event }: { event: ResaCalendarEvent }) => {
	return event.resource.anonymized ? (
		<AnonResaEventRenderer event={event} />
	) : (
		<FullResaEventRenderer event={event} />
	);
};
const AnonResaEventRenderer = ({ event }: { event: ResaCalendarEvent }) => {
	const user = useAtomValue(userAtom(event.resource.users[0].userId));
	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);
	const ref = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		const onResize = () => {
			setWidth(ref.current?.clientWidth ?? 0);
			setHeight(ref.current?.clientHeight ?? 0);
		};
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, []);
	useEffect(() => {
		setWidth(ref.current?.clientWidth ?? 0);
		setHeight(ref.current?.clientHeight ?? 0);
	});
	return (
		<div className={classNames('event-content-wrapper', 'anon')} ref={ref}>
			{user && width > 100 && height > 60 && (
				<span>
					<FormattedMessage
						defaultMessage="{user} est occupé"
						id="O0r6jx"
						values={{ user: user?.display_name }}
					/>
				</span>
			)}
		</div>
	);
};
const FullResaEventRenderer = ({ event }: { event: ResaCalendarEvent }) => {
	const focusResa = useAtomValue(focusResaAtom);
	const location = useAtomValue(locationAtom(event.resource.locationId || 0));
	const allUsers = useAtomValue(allUsersAtom);
	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);
	const ref = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		const onResize = () => {
			setWidth(ref.current?.clientWidth ?? 0);
			setHeight(ref.current?.clientHeight ?? 0);
		};
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, []);
	useEffect(() => {
		setWidth(ref.current?.clientWidth ?? 0);
		setHeight(ref.current?.clientHeight ?? 0);
	});

	const eventStatus = useMemo(
		() =>
			getEventStatus({
				event,
				location,
				focusResa,
				users: event.resource.users
					.map(({ userId }) => allUsers.find((u) => u.ID == userId))
					.filter((x): x is User => !!x),
			}),
		[event, location, focusResa, allUsers],
	);
	const { needsApproval, approved, invalid, awaitingParticipants, ok } =
		eventStatus;

	let cl = '';
	if (needsApproval && !approved) cl = 'awaiting-approval';
	if (awaitingParticipants) cl = 'awaiting-participants';
	if (invalid) cl = 'invalid';
	if (approved || ok) cl = 'ok';

	const sumarize =
		width < 180 ||
		height < 37 ||
		(width < 220 && event.resource.users.length > 3);

	const compact =
		width < 210 ||
		height < 58 ||
		(event.resource.users.length > 2 && width < 320 && height < 70);

	return (
		<div
			className={classNames('event-content-wrapper', cl, { compact })}
			ref={ref}
		>
			{!sumarize ? (
				<ExpandedResaEventRenderer
					event={event}
					eventStatus={eventStatus}
					className={classNames({
						compact,
						vertical: height > width,
					})}
				/>
			) : (
				<SummaryResaEventRenderer
					className={cl}
					vertical={width < height}
					tiny={width < 60 || height < 50}
					location={location}
					event={event}
					eventStatus={eventStatus}
					popover={!isMobile}
				/>
			)}
		</div>
	);
};

const SummaryResaEventRenderer = (props: {
	className: string;
	vertical: boolean;
	tiny: boolean;
	location?: ResaLocation;
	event: ResaCalendarEvent;
	eventStatus: ReturnType<typeof getEventStatus>;
	popover: boolean;
}) => {
	const inner = (
		<div
			className={classNames('event-summary', props.className, {
				vertical: props.vertical,
				tiny: props.tiny,
			})}
		>
			{props.location?.emoji ?? props.location?.post_title.substring(0, 2)}
			{props.event.resource.users.map((u) => (
				<UserLabel key={u.userId} {...u} short={true} />
			))}
		</div>
	);
	if (props.popover)
		return (
			<Popover
				content={
					<ExpandedResaEventRenderer
						event={props.event}
						eventStatus={props.eventStatus}
						className={props.className}
					/>
				}
				color="#f05e39"
				style={{ color: 'white' }}
			>
				{inner}
			</Popover>
		);
	return inner;
};

const ExpandedResaEventRenderer = ({
	event,
	className,
	eventStatus,
}: {
	event: ResaCalendarEvent;
	className: string;
	eventStatus: ReturnType<typeof getEventStatus>;
}) => {
	const focusResa = useAtomValue(focusResaAtom);

	const { needsApproval, approved, invalid, awaitingParticipants } =
		eventStatus;
	return (
		<div className={classNames('focus-event', className)}>
			<div className="location">
				<LocationLabel locationId={event.resource.locationId} />
			</div>
			<div className="times">
				<FormattedMessage
					defaultMessage="{eventStart}-{eventEnd}"
					id="lKW5uZ"
					values={{
						eventStart: (
							<FormattedTime
								value={event.start}
								timeZone="+02:00"
								timeStyle="short"
								hour12={false}
							/>
						),
						eventEnd: (
							<FormattedTime
								value={event.end}
								timeZone="+02:00"
								timeStyle="short"
								hour12={false}
							/>
						),
					}}
				/>
			</div>
			{event.type == 'resa' && (
				<div className="event-data">
					<div className="participants">
						<FormattedList
							type="conjunction"
							value={event.resource.users.map((u) => (
								<UserLabel key={u.userId} {...u} clickable={true} />
							))}
						/>
					</div>

					{!event.resource.anonymized && (
						<div className="notes">{event.resource.notes}</div>
					)}
				</div>
			)}
			<div className="approb-info">
				{invalid && (
					<FormattedMessage
						defaultMessage="En dehors des horaires"
						id="McT4qn"
					/>
				)}
				{!invalid && (
					<>
						{approved && (
							<FormattedMessage defaultMessage="Approuvé" id="n14ftJ" />
						)}
						{awaitingParticipants && (
							<FormattedMessage
								defaultMessage="En attente de participants"
								id="aayx3c"
							/>
						)}
						{!awaitingParticipants && approved === false && (
							<FormattedMessage
								defaultMessage="En attente d'approbation"
								id="NS3ZAF"
							/>
						)}
						{!needsApproval && !awaitingParticipants && 'Validé'}
					</>
				)}
				{focusResa.isAdmin && !event.resource.anonymized && (
					<span className="last-update">
						<FormattedMessage
							defaultMessage="| {date}"
							id="ZVmaCc"
							values={{
								date: (
									<FormattedDate
										timeZone="+02:00"
										value={event.resource.updated}
										month="short"
										day="numeric"
										hour="2-digit"
										minute="2-digit"
										second="2-digit"
										timeZoneName='short'
									/>
								),
							}}
						/>
					</span>
				)}
			</div>
		</div>
	);
};

const getEventStatus = ({
	event: e,
	location: l,
	focusResa,
	users,
}: {
	event: ResaCalendarEvent;
	focusResa: FocusResa;
	location?: ResaLocation;
	users: User[];
}) => {
	const needsApproval = l?.requiresApproval;
	const approved =
		needsApproval !== true
			? undefined
			: !e.resource.anonymized && e.resource.approved != null;
	const inOpenHours =
		l?.openHours != null ? isInOpenHours(e, l.openHours) : undefined;
	const conflictWithMandatoryEvent = focusResa.mandatoryEvents.some(
		(e2) =>
			(e2.start >= e.start && e2.start < e.end) ||
			(e2.end > e.start && e2.end <= e.end),
	);
	const invalid = !inOpenHours || conflictWithMandatoryEvent;
	const hasCosAndPhoto =
		users.length === e.resource.users.length &&
		users.some((u) => u.roles.includes('um_cosplayers')) &&
		users.some(
			(u) =>
				u.roles.includes('um_photographers') ||
				u.roles.includes('um_videographers'),
		);
	const awaitingParticipants =
		e.resource.users.length == 0 ||
		e.resource.users.some((u) => !u.accepted) ||
		!hasCosAndPhoto;
	const ok =
		(!invalid && !awaitingParticipants && needsApproval === false) || approved;
	return {
		needsApproval,
		approved,
		invalid,
		awaitingParticipants,
		ok,
	};
};
